import React, { useEffect, useRef } from 'react';

interface WavelengthBackgroundProps {
  className?: string;
  color?: string;
  secondaryColor?: string;
  speed?: number;
  opacity?: number;
}

/**
 * WavelengthBackground renders a fluid, multi-frequency sine wave animation using HTML5 Canvas.
 * Designed to replace heavy static GIF files in application headers with smooth 60fps organic motion.
 * Optimized for low battery usage via Page Visibility API pause and efficient canvas vector rendering.
 * Reflow-optimized using ResizeObserver contentRect geometry to prevent forced DOM style recalculations.
 */
export default function WavelengthBackground({
  className = 'absolute inset-0 w-full h-full pointer-events-none overflow-hidden opacity-40 z-0',
  color = 'rgba(16, 185, 129, 0.45)', // emerald-500
  secondaryColor = 'rgba(6, 182, 212, 0.3)', // cyan-500
  speed = 1,
  opacity = 0.4,
}: WavelengthBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = 70;
    let animId: number | null = null;
    let isVisible = true;

    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries[0] || !canvas) return;
      const rect = entries[0].contentRect;
      const dpr = window.devicePixelRatio || 1;
      width = rect.width || window.innerWidth;
      height = rect.height || 70;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    });

    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }

    let phaseAlpha = 0;
    let phaseTheta = 2.0;
    let phaseDelta = 4.5;

    const render = () => {
      if (!isVisible) return;
      ctx.clearRect(0, 0, width, height);

      const baseSpeed = 0.8 * speed;
      phaseAlpha += 0.022 * baseSpeed;
      phaseTheta += 0.014 * baseSpeed;
      phaseDelta += 0.009 * baseSpeed;

      const drawWave = (freq: number, amp: number, phase: number, strokeColor: string, lineWidth: number) => {
        ctx.beginPath();
        ctx.lineWidth = lineWidth;
        ctx.strokeStyle = strokeColor;
        const midY = height * 0.5;

        // Step by 3 pixels for high performance while maintaining crisp curves
        for (let x = 0; x <= width; x += 3) {
          const y =
            midY +
            Math.sin(x * freq + phase) * amp +
            Math.cos(x * freq * 0.45 - phase * 0.7) * (amp * 0.35);
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      };

      // Multi-frequency harmonic brainwave entrainment curves (Alpha, Theta, Delta)
      drawWave(0.011, height * 0.28, phaseDelta, secondaryColor, 1.2);
      drawWave(0.016, height * 0.22, phaseTheta, 'rgba(52, 211, 153, 0.25)', 1.5);
      drawWave(0.008, height * 0.34, phaseAlpha, color, 1.8);

      animId = requestAnimationFrame(render);
    };

    const handleVisibilityChange = () => {
      isVisible = !document.hidden;
      if (isVisible && animId === null) {
        render();
      } else if (!isVisible && animId !== null) {
        cancelAnimationFrame(animId);
        animId = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    render();

    return () => {
      if (animId !== null) cancelAnimationFrame(animId);
      resizeObserver.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [color, secondaryColor, speed]);

  return (
    <div className={className} style={{ opacity }} aria-hidden="true">
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
}
